/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { getEnvVar } from "../config/RuntimeConfig";

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
const getAppIdFromUrl = () => {
    try {
        const href = window.location.href;
        const stored = sessionStorage.getItem('preAuthUrl') || href;
        const url = stored.includes('#code=') || stored.includes('?code=') ? href : stored;
        const normalized = url.replace('/fr/', '/').replace('/fr?', '?');
        const parts = normalized.split('on-form');
        if (parts.length > 1) {
            return parts[1].split('/').filter(s => s !== '')[0]?.split('?')[0]?.toLowerCase() || '';
        }
    } catch {}
    return '';
};

const getInitialClientId = () => {
    try {
        const appId = getAppIdFromUrl();
        const key = appId ? `msalClientId_${appId}` : 'msalClientId';
        const saved = sessionStorage.getItem(key);
        if (saved && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(saved)) {
            return saved;
        }
    } catch {}
    return getEnvVar('clientId');
};

const getInitialAuthority = () => {
    const defaultAuthority = "https://login.microsoftonline.com/cddc1229-ac2a-4b97-b78a-0e5cacb5865c";
    try {
        const appId = getAppIdFromUrl();
        const key = appId ? `msalAuthority_${appId}` : 'msalAuthority';
        const saved = sessionStorage.getItem(key);
        if (saved && /^https:\/\/login\.microsoftonline\.com\/[a-zA-Z0-9._-]+\/?$/i.test(saved)) {
            return saved.replace(/\/+$/, "");
        }
    } catch {}
    return defaultAuthority;
};

const getRuntimeCategoryId = () => {
    try {
        const fromProgramContainer = window?.programContainer?.CategoryId;
        if (typeof fromProgramContainer === "string" && fromProgramContainer.trim()) {
            return fromProgramContainer.trim().toLowerCase();
        }
    } catch {}

    return getAppIdFromUrl();
};

const getRedirectUri = () => {
    const base = (getEnvVar('redirectUri') || getEnvVar('redirectUrl') || "").replace(/\/+$/, "");
    const categoryId = getRuntimeCategoryId();

    if (base) {
        if (!categoryId) {
            return `${base}/`;
        }

        const normalizedBase = base.toLowerCase();
        const normalizedCategorySuffix = `/${categoryId}`;
        if (normalizedBase.endsWith(normalizedCategorySuffix) || normalizedBase.endsWith(`${normalizedCategorySuffix}/`)) {
            return base.endsWith("/") ? base : `${base}/`;
        }

        return `${base}/${categoryId}/`;
    }

    return new URL(getEnvVar('PUBLIC_URL') || "/", window.location.origin).toString();
};

export const msalConfig = {
    auth: {
        clientId: getInitialClientId(),
        // clientId: "e44d8e18-1a62-4bbc-ab98-20cb78a7e7f3",
        authority: getInitialAuthority(),
        redirectUri: getRedirectUri()
    },
    cache: {
        cacheLocation: "sessionStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {	
        loggerOptions: {	
            loggerCallback: (level, message, containsPii) => {	
                if (containsPii) {		
                    return;		
                }		
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }	
            }	
        }	
    }
};

// export const protectedResources = {
//     adminAPI: {
//         scopes: {
//             read: ['api://e82f9c8e-ca71-44c2-8e60-4fe24745ea5b/Files.Read', 'api://a7e7335f-9ce5-462f-b085-4d9cb3c86f25/api.read'],
//             write: ['api://e82f9c8e-ca71-44c2-8e60-4fe24745ea5b/Files.Write'],
//         },
//     },
// };

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
// export const loginRequest = {
//   scopes: process.env.REACT_APP_SCOPES?.split(" ") || [],
// };

export const loginRequest = {
  scopes: getEnvVar('REACT_APP_SCOPES')?.split(" ") || []
};

// export const loginRequestCMA = {
//   scopes: ['api://e44d8e18-1a62-4bbc-ab98-20cb78a7e7f3/api.readwrite']
// };

export const graphRequest = {
    scopes: ['User.Read.All']
    // scopes: ['User.Read']
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/1.0/me" //e.g. https://graph.microsoft.com/v1.0/me
};
